import { useEffect, useMemo, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Search as SearchIcon, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Server-side DataTable for any /api list endpoint that supports paginate() shape.
 *
 * Props:
 *  - endpoint: string (e.g. "/products")
 *  - columns: [{ key, label, render?, sortable?, className? }]
 *  - filters: [{ key, label, type: "select"|"date"|"toggle", options?: [{value,label}] }]
 *  - searchPlaceholder: string
 *  - pageSize: number (default 50)
 *  - extraQuery: object (always-on filters merged into request)
 *  - rowKey: string  (default "id")
 *  - emptyMessage: string
 *  - actions: (row) => JSX  per-row action column
 *  - onRowsLoaded: ((rows, total) => void)  callback
 *  - dataTestid: string
 *  - reloadKey: any  external trigger to reload (e.g. after a create)
 */
export default function DataTable({
  endpoint,
  columns,
  filters = [],
  searchPlaceholder = "Search...",
  pageSize: defaultPageSize = 50,
  extraQuery = {},
  rowKey = "id",
  emptyMessage = "No records found",
  actions,
  onRowsLoaded,
  dataTestid = "data-table",
  reloadKey,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [filterValues, setFilterValues] = useState({});
  const [loading, setLoading] = useState(false);

  const buildQuery = useCallback(() => {
    const q = { page, page_size: pageSize };
    if (search) q.search = search;
    if (sortBy) { q.sort_by = sortBy; q.sort_dir = sortDir; }
    Object.entries(filterValues).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined && v !== "_any") q[k] = v;
    });
    Object.entries(extraQuery).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) q[k] = v;
    });
    return q;
  }, [page, pageSize, search, sortBy, sortDir, filterValues, extraQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: buildQuery() });
      const r = data.rows || [];
      setRows(r);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      if (onRowsLoaded) onRowsLoaded(r, data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, buildQuery, onRowsLoaded]);

  useEffect(() => { load(); }, [load, reloadKey]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const onSort = (key, sortable) => {
    if (!sortable) return;
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key); setSortDir("desc");
    }
    setPage(1);
  };

  const onFilterChange = (k, v) => {
    setFilterValues((prev) => ({ ...prev, [k]: v }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilterValues({});
    setSearchInput("");
    setSearch("");
    setSortBy("");
    setPage(1);
  };

  const hasFilters = Object.values(filterValues).some((v) => v && v !== "_any") || search;

  const SortIcon = ({ k, sortable }) => {
    if (!sortable) return null;
    if (sortBy !== k) return <ChevronsUpDown className="w-3 h-3 inline-block ml-1 text-slate-400" />;
    return sortDir === "asc"
      ? <ChevronUp className="w-3 h-3 inline-block ml-1" />
      : <ChevronDown className="w-3 h-3 inline-block ml-1" />;
  };

  return (
    <div className="space-y-3" data-testid={dataTestid}>
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <SearchIcon className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-8 rounded-none h-9"
            data-testid={`${dataTestid}-search`}
          />
          {searchInput && (
            <button type="button" onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid={`${dataTestid}-search-clear`}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {filters.map((f) => {
          if (f.type === "select") {
            return (
              <Select key={f.key} value={filterValues[f.key] || "_any"} onValueChange={(v) => onFilterChange(f.key, v)}>
                <SelectTrigger className="w-40 rounded-none h-9 text-sm" data-testid={`${dataTestid}-filter-${f.key}`}>
                  <SelectValue placeholder={f.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any">{f.label}: Any</SelectItem>
                  {(f.options || []).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          if (f.type === "date") {
            return (
              <Input key={f.key} type="date" value={filterValues[f.key] || ""}
                onChange={(e) => onFilterChange(f.key, e.target.value)}
                placeholder={f.label}
                className="w-40 rounded-none h-9 text-sm"
                title={f.label}
                data-testid={`${dataTestid}-filter-${f.key}`} />
            );
          }
          return null;
        })}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="rounded-none h-9 text-xs"
            data-testid={`${dataTestid}-clear-filters`}>
            <X className="w-3 h-3 mr-1" />Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-slate-500" data-testid={`${dataTestid}-total`}>
          {loading ? "Loading..." : `${total.toLocaleString()} record${total !== 1 ? "s" : ""}`}
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 text-left font-medium text-slate-600 ${c.sortable ? "cursor-pointer select-none hover:bg-slate-100" : ""} ${c.className || ""}`}
                  onClick={() => onSort(c.sortKey || c.key, c.sortable)}
                  data-testid={`${dataTestid}-col-${c.key}`}
                >
                  {c.label}<SortIcon k={c.sortKey || c.key} sortable={c.sortable} />
                </th>
              ))}
              {actions && <th className="px-3 py-2 text-right font-medium text-slate-600">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="px-3 py-8 text-center text-slate-400">{loading ? "Loading..." : emptyMessage}</td></tr>
            ) : rows.map((row) => (
              <tr key={row[rowKey]} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`${dataTestid}-row-${row[rowKey]}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.className || ""}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
                {actions && <td className="px-3 py-2 text-right">{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <span>Page</span>
          <Input type="number" min={1} max={pages} value={page}
            onChange={(e) => {
              const v = parseInt(e.target.value || "1", 10);
              if (Number.isFinite(v) && v >= 1 && v <= pages) setPage(v);
            }}
            className="w-16 rounded-none h-8 text-center"
            data-testid={`${dataTestid}-page-input`} />
          <span>of {pages.toLocaleString()}</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v, 10)); setPage(1); }}>
            <SelectTrigger className="w-24 rounded-none h-8" data-testid={`${dataTestid}-page-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page <= 1}
            className="rounded-none h-8 text-xs" data-testid={`${dataTestid}-first`}>« First</Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="rounded-none h-8" data-testid={`${dataTestid}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
            className="rounded-none h-8" data-testid={`${dataTestid}-next`}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setPage(pages)} disabled={page >= pages}
            className="rounded-none h-8 text-xs" data-testid={`${dataTestid}-last`}>Last »</Button>
        </div>
      </div>
    </div>
  );
}
